import { type PartialDeep } from 'type-fest';

import {
  type CanvasElementFrom,
  type HeightSettings,
  type WidthSettings,
} from '@blaze-runtime/engine-domain';
import { type BlazeSchema } from '@blaze-shared/schema-domain';

import { type DefinitionMapper } from '../../../ai';
import { extractCommonFormFields } from '../../../ai/common/extract-common-form-fields';
import { type WidthStyle } from '../../../ai/models/styles';
import { type CanvasElementDefinition } from '../../../canvas-elements/element-definition';

import { type QrCodeDefinition, type QrCodeUrlTarget } from './qr-code.definition';

export class QrCodeDefinitionMapper implements DefinitionMapper<'qrcode'> {
  readonly type = 'qrcode';

  extract(
    element: CanvasElementFrom<'qrcode'>,
    _schemas: Readonly<Record<string, BlazeSchema>>,
    _definition: CanvasElementDefinition<'qrcode'>,
  ): PartialDeep<QrCodeDefinition> {
    const { url } = element.props.content;

    return {
      ...extractCommonFormFields(element),
      field: url.type === 'dynamic' ? extractFieldName(element) : undefined,
      url: toUrlTarget(url),
      width: toQrCodeWidthStyle(element.width),
      height: toQrCodeHeightStyle(element.height),
    };
  }
}

function toUrlTarget(url: {
  type: 'dynamic' | 'add' | 'current';
  value: string;
}): QrCodeUrlTarget | undefined {
  if (url.type === 'add') {
    return { $type: 'url', value: url.value };
  }

  if (url.type === 'current') {
    return { $type: 'currentPage' };
  }

  return undefined;
}

function extractFieldName(element: CanvasElementFrom<'qrcode'>): string | undefined {
  const binding = element.props.data?.find((entry) => entry.key === 'value');

  return binding?.dataSource?.sourceType === 'formContainer'
    ? binding.dataSource.columns[0]?.name
    : undefined;
}

function toQrCodeWidthStyle(width: WidthSettings): WidthStyle | undefined {
  if (width.mode === 'full') {
    return { $type: 'full' };
  }

  if (width.mode === 'value' && width.fixed && width.value !== undefined) {
    return { $type: 'fixed', width: width.value };
  }

  return undefined;
}

function toQrCodeHeightStyle(height: HeightSettings | undefined): number | undefined {
  return height?.mode === 'value' && height.fixed && height.value !== undefined
    ? height.value
    : undefined;
}
